import { useEffect, useRef, useState } from 'react';

type Size = { width?: number; height?: number } | null | undefined;

interface UseContainerReadyOptions {
  minWidth?: number;
  minHeight?: number;
  debounce?: number;
}

export function useContainerReady(
  size: Size,
  { minWidth = 40, minHeight = 40, debounce = 120 }: UseContainerReadyOptions = {},
) {
  const [ready, setReady] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const width = typeof size?.width === 'number' ? size.width : 0;
    const height = typeof size?.height === 'number' ? size.height : 0;
    const meetsThreshold = width >= minWidth && height >= minHeight;

    if (!meetsThreshold) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setReady(prev => {
        if (!prev) {
          return prev;
        }
        return false;
      });

      return undefined;
    }

    if (ready) {
      return undefined;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setReady(true);
      timeoutRef.current = null;
    }, debounce);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [ready, size?.width, size?.height, minWidth, minHeight, debounce]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return ready;
}

export default useContainerReady;
